<template>
	<Col
		:span="24"
		:xl="{ span: 12 }"
	>
		<Spin :spinning="isLoading">
			<Card>
				<VerticalLayout>
					<div class="flex-wrapper">
						<Switch v-model:checked="isEnabled" class="switch" @change="onEnabledChange"/>
						<TypographyTitle :level="4" class="flex-grow">{{ $t('external-sources.headers.' + labelRef) }}</TypographyTitle>
						<StatusDisplay
							:source="statusDisplay"
							prefix="external-sources.statuses"
							:badgeDict="statusBadgeDict"
						/>
					</div>
					<Form
						:model="formState"
						:label-col="{ span: 12 }"
						:wrapper-col="{ span: 12 }"
						:colon="false"
					>
						<InfoInlineHint v-if="showBaselinesAlert">
							<span>
								{{ $t('external-sources.messages.baselineBrdcInfo') }}
							</span>
						</InfoInlineHint>
						<FormItem
							:label="$t('external-sources.fields.mntpntSrc')"
							v-bind="validateInfos.mntpntSrc"
						>
							<InputRestricted v-model="formState.mntpntSrc" :allowedFormatChecker="mountpointChecker" :toolTipText="$t('validators.mountpointMessage')" />
						</FormItem>

						<FormItem
							:label="$t('external-sources.fields.host')"
							v-bind="validateInfos.host"
						>
							<InputRestricted v-model="formState.host" :allowedFormatChecker="hostnameChecker" :toolTipText="$t('validators.hostnameMessage')" />
						</FormItem>
						<FormItem
							:label="$t('external-sources.fields.port')"
							v-bind="validateInfos.port"
						>
							<InputDecimal v-model="formState.port" :allowPoint="false" range="port" />
						</FormItem>
						<FormItem
							:label="$t('external-sources.fields.protocolSecure')"
							v-bind="validateInfos.protocolSecure"
						>
							<RadioGroup v-model:value="formState.protocolSecure" :options="secureProtocolOptions" option-type="button" />
						</FormItem>

						<FormItem
							:label="$t('external-sources.fields.username')"
							v-bind="validateInfos.username"
						>
							<InputRestricted v-model="formState.username" :allowedFormatChecker="alphaNumericChecker" :toolTipText="$t('validators.alphaNumericMessage')" />
						</FormItem>
						<FormItem
							:label="$t('external-sources.fields.password')"
							v-bind="validateInfos.password"
						>
							<Input v-model:value="formState.password" />
						</FormItem>
					</Form>
					<div v-if="defaultValue != null">
						<DividerAutoDirection>{{ $t('external-sources.defaultsDivider') }}</DividerAutoDirection>
						<Descriptions size="small" :column="1" :bordered="true" :colon="false" :style="{ background: 'white' }">
							<DescriptionsItem :label="$t('external-sources.fields.mntpntSrc')">{{ defaultValue.mntpntSrc }}</DescriptionsItem>
							<DescriptionsItem :label="$t('external-sources.fields.host')">{{ defaultValue.host }}</DescriptionsItem>
							<DescriptionsItem :label="$t('external-sources.fields.port')">{{ defaultValue.port }}</DescriptionsItem>
							<DescriptionsItem :label="$t('external-sources.fields.protocolSecure')">{{ defaultValue.protocolSecure ? 'ntrips' : 'ntrip' }}</DescriptionsItem>
							<DescriptionsItem :label="$t('external-sources.fields.username')">{{ defaultValue.username }}</DescriptionsItem>
							<DescriptionsItem :label="$t('external-sources.fields.password')">{{ defaultValue.password }}</DescriptionsItem>
						</Descriptions>
					</div>
				</VerticalLayout>
				<template #actions>
					<div class="flex-wrapper-auto pd-md-x">
						<Button v-if="defaultValue != null" key="defaults" @click="onApplyDefaults" :disabled="isLoading || isUpdating" :loading="isLoading || isUpdating">{{ $t('external-sources.applyDefaultsButtonLabel') }}</Button>
						<div class="flex-grow"></div>
						<Button type="primary" @click.prevent="onSubmit" :disabled="isLoading || isUpdating" :loading="isLoading || isUpdating">{{ $t('external-sources.submitButtonLabel') }}</Button>
					</div>
				</template>
			</Card>
		</Spin>
	</Col>
</template>

<script>
import { reactive, ref, toRef, computed } from 'vue';
import camelCase from 'lodash/camelCase';

import {
	Form, FormItem, Input, Button, Col, Card, TypographyTitle, Switch,
	Spin, RadioGroup, Descriptions, DescriptionsItem, message
} from 'ant-design-vue';

import { useI18n } from 'vue-i18n'

import Api from '@/api/Api';
import { toCamelCase } from '@/api/ApiHelpers';
import Utils from '@/api/Utils';
import Validators from '@/api/Validators';

import { showErrorMessage } from '@/components/common/errorMessage';
import { useInterval } from '@/utils/useInterval';

import InfoInlineHint from '@/components/common/InfoInlineHint';
import InputDecimal from '@/components/common/InputDecimal';
import InputRestricted from '@/components/common/InputRestricted';
import VerticalLayout from '@/components/common/VerticalLayout';
import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import StatusDisplay from '@/components/common/StatusDisplay.vue';

import { useLicenseStore } from '@/stores/licenseStore';

const STATUS_BADGES = {
	updating: 'processing',
	working: 'success',
	stopped: 'default',
	failed: 'error',
};


export default {
	components: {
		Form, FormItem, Input, Button, Col, Card, TypographyTitle, Switch, Spin, RadioGroup,
		Descriptions, DescriptionsItem,
		InputDecimal, InputRestricted, VerticalLayout, InfoInlineHint,
		DividerAutoDirection, StatusDisplay,
	},
	props: ['label'],
	setup(props) {
		const { t } = useI18n();
		const labelRef = toRef(props, 'label');
		const isEnabled = ref(false);
		const formState = reactive({
			status: null,
			statusOnline: null,
			host: '',
			port: null,
			protocolSecure: false,
			username: '',
			password: '',
			mntpntSrc: '',
		});
		const defaultValue = ref(null);
		const isLoading = ref(true);
		const {
			validate,
			validateInfos,
		} = Validators.useExternalSourceFormRules(formState);
		const statusDisplay = computed(() => {
			if (formState.status == null) {
				return 'updating';
			}
			if (formState.status == 'working') {
				if (formState.statusOnline === 'no_data') {
					return 'updating';
				}
				if (formState.statusOnline === 'online') {
					return 'working';
				}
				if (formState.statusOnline === 'broken') {
					return 'failed';
				}
			}
			return formState.status;
		})
		const isUpdating = computed(() => statusDisplay.value === 'updating');
		const isCheckingStatus = ref(true);
		const loadSourceDetails = () => {
			if (!isCheckingStatus.value) {
				return;
			}
			isLoading.value = true;
			Api.vrsExternalSourceGet(labelRef.value, response => {
				if (response.status === 'ok') {
					const data = toCamelCase(response.value);
					Object.assign(formState, data);
					defaultValue.value = (response.default == null) ? null : toCamelCase(response.default);
					isEnabled.value = data.status === 'working';
					isCheckingStatus.value = isUpdating.value;
				} else {
					showErrorMessage(response.errors);
				}
				isLoading.value = false;
			});
		}
		const statusBadgeDict = ref(STATUS_BADGES);
		useInterval(loadSourceDetails, 10000, true);
		const onSubmit = () => {
			validate().then(() => {
				isLoading.value = true;
				formState.status = 'working';
				formState.statusOnline = 'no_data';
				isEnabled.value = true;
				const data = Utils.toObject(formState);
				data.label = labelRef.value;
				Api.vrsExternalSourcePost(data, response => {
					if (response.status === 'ok') {
						message.success(t('external-sources.messages.success'));
						isCheckingStatus.value = true;
					} else {
						['host', 'port', 'username', 'password', 'mntpnt_src'].forEach(element => {
							if (response.errors[element]) {
								let jsElement = camelCase(element);
								validateInfos[jsElement].validateStatus = 'error';
								validateInfos[jsElement].help = response.errors[element];
								delete response.errors[element];
							}
						});
						showErrorMessage(response.errors);
					}
					isLoading.value = false;
				});
			});
		};
		const onApplyDefaults = () => {
			isLoading.value = true;
			formState.status = 'working';
			formState.statusOnline = 'no_data';
			isEnabled.value = true;
			Api.vrsExternalSourceApplyDefaults(labelRef.value, response => {
				if (response.status === 'ok') {
					message.success(t('external-sources.messages.success'));
					isCheckingStatus.value = true;
					loadSourceDetails();
				} else {
					showErrorMessage(response.errors);
					isLoading.value = false;
				}
			});
		};
		const enabledChangeCore = enabled => {
			isLoading.value = true;
			formState.status = enabled ? 'working' : 'stopped';
			formState.statusOnline = 'no_data';
			isEnabled.value = enabled;
			Api.vrsExternalSourceEnable(labelRef.value, enabled, response => {
				if (response.status === 'ok') {
					message.success(t('external-sources.messages.success'));
					if (response.warning) {
						// это не ошибка, а предупреждение
						// пока используем, чтобы уведомить пользователя, что cssr + Brdc disabled если выключили source brdc
						message.warning(t('external-sources.messages.' + response.warning), 7);
					}
					isCheckingStatus.value = true;
				} else {
					showErrorMessage(response.errors);
				}
				isLoading.value = false;
			});
		}
		const onEnabledChange = enabled => {
			if (enabled) {
				validate().then(() => {
					enabledChangeCore(enabled);
				}).catch(() => {
					isEnabled.value = false;
					message.error(t('external-sources.messages.invalidFields'));
				});
			} else {
				enabledChangeCore(enabled);
			}
		}
		const secureProtocolOptions = ref([
			{ label: 'ntrip', value: false },
			{ label: 'ntrips', value: true },
		]);
		const licenseStore = useLicenseStore();
		const showBaselinesAlert = computed(() => labelRef.value === 'brdc' && licenseStore.allowBaselines);
		return {
			labelRef,
			isEnabled,
			statusDisplay,
			statusBadgeDict,
			formState,
			defaultValue,
			validateInfos,
			isLoading,
			isUpdating,
			onSubmit,
			onApplyDefaults,
			onEnabledChange,
			secureProtocolOptions,
			showBaselinesAlert,
			alphaNumericChecker: Validators.alphaNumericChecker,
			mountpointChecker: Validators.mountpointChecker,
			hostnameChecker: Validators.hostnameChecker,
		};
	},
}
</script>

<style scoped>
.switch {
	margin: 5px 10px 0;
}
</style>